import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { LoginPage } from './components/LoginPage';
import { FlagProtectedRoute } from './components/FlagProtectedRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RestrictedAccessPage } from './components/RestrictedAccessPage';
import { RoleProtectedRoute } from './components/RoleProtectedRoute';
import { RootRedirect } from './components/RootRedirect';
import {
  AcquirePage,
  ApiPage,
  ComingSoon,
  CommunicationDetailPage,
  CommunicationsPage,
  CredentialsListPage,
  DataIntegrationPage,
  EngageSetup,
  MeasurePage,
  PartnerMetricsPage,
  ProfilePage,
  ReputationPage,
  TeamPage,
  TenantDetail,
  TenantsPage,
  ThemePage,
  ToolEditPage,
  ToolsListPage,
  UserDetailPage,
  UsersPage,
  VoiceNavigator,
} from './pages';
import { appRoutes } from './app-routes';

function App() {
  return (
    <div className="App">
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Restricted access page for users without proper role */}
        <Route
          path="/restricted"
          element={
            <ProtectedRoute>
              <RestrictedAccessPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with persistent layout and sidebar - require admin role */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <Navigate to="/dashboard/metrics" replace />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/metrics"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <PartnerMetricsPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/acquire"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <AcquirePage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/measure"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <MeasurePage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/reputation"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <ReputationPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={`${appRoutes.VOICE}/:tab?`}
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <VoiceNavigator />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <ProfilePage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/team"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <TeamPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/data-integration/:tab?"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <DataIntegrationPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/theme"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <ThemePage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/api"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <ApiPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/tenants"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <TenantsPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/tenants/:id/:tab?"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <TenantDetail />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/communications"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <CommunicationsPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/communications/:id"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <CommunicationDetailPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/users"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <UsersPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/users/:externalId"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <UserDetailPage />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={`${appRoutes.ENGAGE_SETUP}/*`}
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <EngageSetup />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/ai-tools"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <FlagProtectedRoute flag="enable-partner-mcp-authoring">
                  <Layout>
                    <ToolsListPage />
                  </Layout>
                </FlagProtectedRoute>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/ai-tools/new"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <FlagProtectedRoute flag="enable-partner-mcp-authoring">
                  <Layout>
                    <ToolEditPage />
                  </Layout>
                </FlagProtectedRoute>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/ai-tools/:id"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <FlagProtectedRoute flag="enable-partner-mcp-authoring">
                  <Layout>
                    <ToolEditPage />
                  </Layout>
                </FlagProtectedRoute>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/ai-credentials"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <FlagProtectedRoute flag="enable-partner-mcp-authoring">
                  <Layout>
                    <CredentialsListPage />
                  </Layout>
                </FlagProtectedRoute>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        {/* Generic Coming Soon route for not-yet-implemented pages */}
        <Route
          path="/dashboard/coming-soon"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <ComingSoon />
                </Layout>
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        {/* Default redirect - checks auth state */}
        <Route path="/" element={<RootRedirect />} />

        {/* Catch all - redirect to root for proper auth handling */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
